import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";
import AdminImagesPanel from "./ImagesPanel";
import Autocomplete from "@mui/material/Autocomplete";

type ExtraRow = { id?: number; title: string; value: string };
type FeatureRow = { id: number; name: string };

function calcCompleteness(p: any) {
  const missing = [
    !p.latitude,
    !p.longitude,
    !p.featured_image_url,
    !p.content,
    p.persons_max == null,
    p.price_per_night == null,
  ].filter(Boolean).length;
  const total = 6;
  const pct = Math.round(((total - missing) / total) * 100);
  return { pct, missing };
}

function FormGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
      }}
    >
      {children}
    </Box>
  );
}

function Col({
  xs = 12,
  md,
  children,
}: {
  xs?: number;
  md?: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        gridColumn: {
          xs: "1 / -1",
          md: `span ${md ?? xs}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

function slugify(input: string) {
  return (input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 190);
}

function makeEmptyProperty() {
  return {
    title: "",
    slug: "",
    status: "draft",
    listing_status: "rent",
    property_type: "apartment",
    label: "",
    city: "",
    region: "",
    neighborhood: "",
    address: "",
    zip: "",

    latitude: null as number | null,
    longitude: null as number | null,

    bedrooms: null as number | null,
    bathrooms: null as number | null,
    size_m2: null as number | null,

    persons_min: 1,
    persons_max: 2,

    price_per_night: null as number | null,
    min_price_per_night: null as number | null,
    min_stay_nights: 1,

    featured_image_url: "",
    excerpt: "",
    content: "",
  };
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const LISTING_OPTIONS = [
  { value: "rent", label: "Rent" },
  { value: "sale", label: "Sale" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Apartment" },
  { value: "house", label: "House" },
  { value: "villa", label: "Villa" },
  { value: "studio", label: "Studio" },
  { value: "maisonette", label: "Maisonette" },
  { value: "land", label: "Land" },
  { value: "hotel", label: "Hotel" },
  { value: "other", label: "Other" },
];

export default function AdminNewPropertyPage() {
  const nav = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const currentLocale = locale || "el";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [tab, setTab] = React.useState(0);
  const [createdId, setCreatedId] = React.useState<number | null>(null);

  const [item, setItem] = React.useState<any>(makeEmptyProperty());
  const [extras, setExtras] = React.useState<ExtraRow[]>([]);
  const [allFeatures, setAllFeatures] = React.useState<FeatureRow[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = React.useState<number[]>(
    [],
  );

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);

  const [extrasCatalog, setExtrasCatalog] = React.useState<{ title: string }[]>(
    [],
  );

  const [slugTouched, setSlugTouched] = React.useState(false);
  React.useEffect(() => {
    if (!slugTouched) {
      setItem((s: any) => ({ ...s, slug: slugify(s.title) }));
    }
  }, [item.title, slugTouched]);

  const [coordsText, setCoordsText] = React.useState("");
  const set = (k: string, v: any) => setItem((s: any) => ({ ...s, [k]: v }));

  React.useEffect(() => {
    adminFetch(`/api/admin/extras_catalog`)
      .then(async (r) => {
        if (!r.ok) return;
        const d = await r.json().catch(() => ({}));
        setExtrasCatalog(Array.isArray(d.items) ? d.items : []);
      })
      .catch(() => null);
  }, []);

  const addExtra = () => setExtras((s) => [...s, { title: "", value: "" }]);
  const removeExtra = (idx: number) =>
    setExtras((s) => s.filter((_, i) => i !== idx));
  const updateExtra = (idx: number, k: keyof ExtraRow, v: any) =>
    setExtras((s) => s.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

  const applyCoords = () => {
    const raw = (coordsText || "").trim();
    if (!raw) return;

    const parts = raw.split(/[\s,]+/).filter(Boolean);
    if (parts.length < 2) {
      setErr("Δώσε συντεταγμένες ως: 40.25..., 23.51...");
      return;
    }
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setErr("Μη έγκυρες συντεταγμένες.");
      return;
    }
    setErr(null);
    set("latitude", lat);
    set("longitude", lng);
  };

  const createDraft = async () => {
    setErr(null);
    setOk(null);
    setSaving(true);

    try {
      const res = await adminFetch("/api/admin/properties", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: item.title,
          slug: item.slug,
          listing_status: item.listing_status || "rent",
          status: item.status || "draft",
          property_type: item.property_type || "apartment",

          label: item.label,
          city: item.city,
          region: item.region,
          neighborhood: item.neighborhood,
          address: item.address,
          zip: item.zip,

          latitude: item.latitude != null ? Number(item.latitude) : null,
          longitude: item.longitude != null ? Number(item.longitude) : null,

          bedrooms: item.bedrooms != null ? Number(item.bedrooms) : null,
          bathrooms: item.bathrooms != null ? Number(item.bathrooms) : null,
          size_m2: item.size_m2 != null ? Number(item.size_m2) : null,

          persons_min: item.persons_min != null ? Number(item.persons_min) : 1,
          persons_max: item.persons_max != null ? Number(item.persons_max) : 2,

          price_per_night:
            item.price_per_night != null ? Number(item.price_per_night) : null,
          min_price_per_night:
            item.min_price_per_night != null
              ? Number(item.min_price_per_night)
              : null,
          min_stay_nights:
            item.min_stay_nights != null ? Number(item.min_stay_nights) : 1,

          featured_image_url: item.featured_image_url,
          excerpt: item.excerpt,
          content: item.content,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία δημιουργίας.");
        return;
      }

      const newId = Number(data?.item?.id);
      if (!newId) {
        setErr("Δεν επέστρεψε ID από τον server.");
        return;
      }

      setCreatedId(newId);
      setOk(
        "Δημιουργήθηκε! Τώρα μπορείς να ανεβάσεις φωτογραφίες & να συμπληρώσεις τα υπόλοιπα.",
      );

      setItem((s: any) => ({ ...s, id: newId }));
      setTab(1);
    } catch {
      setErr("Αποτυχία δημιουργίας.");
    } finally {
      setSaving(false);
    }
  };

  const saveAfterCreate = async () => {
    if (!createdId) return;
    setErr(null);
    setOk(null);
    setSaving(true);

    try {
      const res = await adminFetch(`/api/admin/properties/${createdId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: item.title,
          slug: item.slug,
          listing_status: item.listing_status,
          status: item.status,
          property_type: item.property_type,
          label: item.label,

          city: item.city,
          region: item.region,
          neighborhood: item.neighborhood,
          address: item.address,
          zip: item.zip,

          latitude: item.latitude != null ? Number(item.latitude) : null,
          longitude: item.longitude != null ? Number(item.longitude) : null,

          bedrooms: item.bedrooms != null ? Number(item.bedrooms) : null,
          bathrooms: item.bathrooms != null ? Number(item.bathrooms) : null,
          size_m2: item.size_m2 != null ? Number(item.size_m2) : null,

          persons_min:
            item.persons_min != null ? Number(item.persons_min) : null,
          persons_max:
            item.persons_max != null ? Number(item.persons_max) : null,

          price_per_night:
            item.price_per_night != null ? Number(item.price_per_night) : null,
          min_price_per_night:
            item.min_price_per_night != null
              ? Number(item.min_price_per_night)
              : null,
          min_stay_nights:
            item.min_stay_nights != null ? Number(item.min_stay_nights) : 1,

          featured_image_url: item.featured_image_url,
          excerpt: item.excerpt,
          content: item.content,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία αποθήκευσης.");
        return;
      }

      await adminFetch(`/api/admin/properties/${createdId}/extras`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: extras }),
      }).catch(() => null);

      await adminFetch(`/api/admin/properties/${createdId}/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature_ids: selectedFeatureIds }),
      }).catch(() => null);

      setOk("Αποθηκεύτηκε.");
    } finally {
      setSaving(false);
    }
  };

  const c = calcCompleteness(item);

  return (
    <Stack spacing={2}>
      {/* Header (responsive) */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
      >
        <Stack spacing={0.3}>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            Admin · New Property
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {createdId ? `#${createdId}` : "Δεν έχει δημιουργηθεί ακόμα (Create πρώτα)"}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems="stretch"
        >
          <Chip
            label={`Completeness ${c.pct}%`}
            color={c.pct >= 80 ? "success" : c.pct >= 60 ? "warning" : "error"}
            variant="outlined"
            sx={{ alignSelf: { sm: "center" } }}
          />

          <Button
            fullWidth={isMobile}
            variant="outlined"
            onClick={() => nav(`/${currentLocale}/admin/properties`)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Λίστα
          </Button>

          {!createdId ? (
            <Button
              fullWidth={isMobile}
              variant="contained"
              onClick={createDraft}
              disabled={saving}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "Creating..." : "Create"}
            </Button>
          ) : (
            <Button
              fullWidth={isMobile}
              variant="contained"
              onClick={saveAfterCreate}
              disabled={saving}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </Stack>
      </Stack>

      {err ? <Alert severity="error">{err}</Alert> : null}
      {ok ? <Alert severity="success">{ok}</Alert> : null}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              minHeight: 48,
              fontWeight: 800,
            },
          }}
        >
          <Tab label="Basics" />
          <Tab label="Gallery" />
          <Tab label="Extras" />
          <Tab label="Features" />
        </Tabs>

        <Divider />

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {/* TAB 0: BASICS */}
          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Βασικά</Typography>

              <FormGrid>
                <Col xs={12} md={6}>
                  <TextField
                    size="small"
                    label="Title"
                    fullWidth
                    value={item.title || ""}
                    onChange={(e) => set("title", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={6}>
                  <TextField
                    size="small"
                    label="Slug"
                    fullWidth
                    value={item.slug || ""}
                    onChange={(e) => {
                      setSlugTouched(true);
                      set("slug", e.target.value);
                    }}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={item.status || "draft"}
                      onChange={(e) => set("status", String(e.target.value))}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Listing</InputLabel>
                    <Select
                      label="Listing"
                      value={item.listing_status || "rent"}
                      onChange={(e) => set("listing_status", String(e.target.value))}
                    >
                      {LISTING_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Property type</InputLabel>
                    <Select
                      label="Property type"
                      value={item.property_type || "apartment"}
                      onChange={(e) => set("property_type", String(e.target.value))}
                    >
                      {PROPERTY_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>

                <Col xs={12} md={6}>
                  <TextField
                    size="small"
                    label="Coordinates (lat, lng)"
                    placeholder="40.255364..., 23.517464..."
                    fullWidth
                    value={coordsText}
                    onChange={(e) => setCoordsText(e.target.value)}
                    onBlur={applyCoords}
                    helperText="Κάνε paste από Google Maps (lat, lng). Με blur γεμίζει Latitude/Longitude."
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Latitude"
                    type="number"
                    fullWidth
                    value={item.latitude ?? ""}
                    onChange={(e) =>
                      set("latitude", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Longitude"
                    type="number"
                    fullWidth
                    value={item.longitude ?? ""}
                    onChange={(e) =>
                      set("longitude", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    size="small"
                    label="City"
                    fullWidth
                    value={item.city || ""}
                    onChange={(e) => set("city", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    size="small"
                    label="Region"
                    fullWidth
                    value={item.region || ""}
                    onChange={(e) => set("region", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <TextField
                    size="small"
                    label="Label"
                    fullWidth
                    value={item.label || ""}
                    onChange={(e) => set("label", e.target.value)}
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    size="small"
                    label="Address"
                    fullWidth
                    value={item.address || ""}
                    onChange={(e) => set("address", e.target.value)}
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    size="small"
                    label="Beds"
                    type="number"
                    fullWidth
                    value={item.bedrooms ?? ""}
                    onChange={(e) =>
                      set("bedrooms", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    size="small"
                    label="Baths"
                    type="number"
                    fullWidth
                    value={item.bathrooms ?? ""}
                    onChange={(e) =>
                      set("bathrooms", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={2}>
                  <TextField
                    size="small"
                    label="Size m²"
                    type="number"
                    fullWidth
                    value={item.size_m2 ?? ""}
                    onChange={(e) =>
                      set("size_m2", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Persons min"
                    type="number"
                    fullWidth
                    value={item.persons_min ?? ""}
                    onChange={(e) =>
                      set("persons_min", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Persons max"
                    type="number"
                    fullWidth
                    value={item.persons_max ?? ""}
                    onChange={(e) =>
                      set("persons_max", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Price / night"
                    type="number"
                    fullWidth
                    value={item.price_per_night ?? ""}
                    onChange={(e) =>
                      set("price_per_night", e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </Col>

                <Col xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Min stay nights"
                    type="number"
                    fullWidth
                    value={item.min_stay_nights ?? 1}
                    onChange={(e) =>
                      set("min_stay_nights", e.target.value ? Number(e.target.value) : 1)
                    }
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    size="small"
                    label="Excerpt"
                    fullWidth
                    multiline
                    minRows={2}
                    value={item.excerpt || ""}
                    onChange={(e) => set("excerpt", e.target.value)}
                  />
                </Col>

                <Col xs={12}>
                  <TextField
                    size="small"
                    label="Content (HTML)"
                    fullWidth
                    multiline
                    minRows={6}
                    value={item.content || ""}
                    onChange={(e) => set("content", e.target.value)}
                  />
                </Col>
              </FormGrid>

              {!createdId ? (
                <Alert severity="info">
                  Πρώτα πάτα <b>Create</b> για να δημιουργηθεί το ακίνητο και να ενεργοποιηθούν Gallery/Extras/Features.
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {/* TAB 1: GALLERY */}
          {tab === 1 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Photos</Typography>

              {!createdId ? (
                <Alert severity="info">
                  Πρώτα πάτα <b>Create</b> και μετά ανέβασε φωτογραφίες.
                </Alert>
              ) : (
                <AdminImagesPanel propertyId={Number(item.id)} />
              )}
            </Stack>
          ) : null}

          {/* TAB 2: EXTRAS */}
          {tab === 2 ? (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
              >
                <Typography sx={{ fontWeight: 900 }}>Extras</Typography>
                <Button fullWidth={isMobile} variant="outlined" onClick={addExtra}>
                  Add extra
                </Button>
              </Stack>

              {!createdId ? (
                <Alert severity="info">
                  Πρώτα πάτα <b>Create</b>. Μετά συμπλήρωσε Extras και κάνε Save.
                </Alert>
              ) : null}

              {extras.map((ex, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <FormGrid>
                    <Col xs={12} md={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Extra</InputLabel>
                        <Select
                          label="Extra"
                          value={ex.title || ""}
                          onChange={(e) =>
                            updateExtra(idx, "title", String(e.target.value))
                          }
                        >
                          <MenuItem value="">
                            <em>Custom…</em>
                          </MenuItem>
                          {extrasCatalog.map((x) => (
                            <MenuItem key={x.title} value={x.title}>
                              {x.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {(!ex.title ||
                        !extrasCatalog.some((c) => c.title === ex.title)) ? (
                        <TextField
                          size="small"
                          sx={{ mt: 1 }}
                          label="Custom title"
                          fullWidth
                          value={ex.title || ""}
                          onChange={(e) => updateExtra(idx, "title", e.target.value)}
                        />
                      ) : null}
                    </Col>

                    <Col xs={12} md={5}>
                      <TextField
                        size="small"
                        label="Value"
                        fullWidth
                        placeholder="π.χ. 4-5 / Sea view / Yes / 10-12"
                        value={ex.value || ""}
                        onChange={(e) => updateExtra(idx, "value", e.target.value)}
                      />
                    </Col>

                    <Col xs={12} md={2}>
                      <Button
                        fullWidth={isMobile}
                        variant="text"
                        color="error"
                        onClick={() => removeExtra(idx)}
                        sx={{ justifyContent: { md: "flex-end" } }}
                      >
                        Remove
                      </Button>
                    </Col>
                  </FormGrid>
                </Paper>
              ))}

              <Divider />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
              >
                <Typography sx={{ fontWeight: 800 }}>Manage Extras</Typography>
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  onClick={() => nav(`/${currentLocale}/admin/extras`)}
                >
                  Open Extras Manager
                </Button>
              </Stack>
            </Stack>
          ) : null}

          {/* TAB 3: FEATURES */}
          {tab === 3 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 900 }}>Features</Typography>

              {!createdId ? (
                <Alert severity="info">
                  Πρώτα πάτα <b>Create</b>. Μετά μπορείς να επιλέξεις Features και να κάνεις Save.
                </Alert>
              ) : null}

              {allFeatures.length === 0 ? (
                <Alert severity="info">
                  Δεν βρέθηκαν features (χρειάζεται endpoint /api/admin/features).
                </Alert>
              ) : (
                <Autocomplete
                  multiple
                  options={allFeatures}
                  getOptionLabel={(o) => o.name}
                  value={allFeatures.filter((f) => selectedFeatureIds.includes(f.id))}
                  onChange={(_, arr) => setSelectedFeatureIds(arr.map((x) => x.id))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                        sx={{ fontWeight: 700 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select features"
                      placeholder="Type to search…"
                    />
                  )}
                />
              )}

              <Divider />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
              >
                <Typography sx={{ fontWeight: 800 }}>Manage Features</Typography>
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  onClick={() => nav(`/${currentLocale}/admin/features`)}
                >
                  Open Features Manager
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Paper>
    </Stack>
  );
}
